import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, CheckCircle, DollarSign, Eye, MessageSquare, X, CheckCheck, PhoneMissed } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 30_000; // 30 seconds

const TYPE_CONFIG = {
  quote_approved:   { icon: CheckCircle,   color: 'text-green-600',   bg: 'bg-green-50' },
  payment_received: { icon: DollarSign,    color: 'text-emerald-600', bg: 'bg-emerald-50' },
  quote_viewed:     { icon: Eye,           color: 'text-orange-500',  bg: 'bg-orange-50' },
  new_sms:          { icon: MessageSquare, color: 'text-blue-500',    bg: 'bg-blue-50' },
  missed_call:      { icon: PhoneMissed,   color: 'text-red-500',     bg: 'bg-red-50' },
};
const DEFAULT_CFG = { icon: Bell, color: 'text-gray-500', bg: 'bg-gray-100' };

const formatTimeAgo = (iso) => {
  if (!iso) return '';
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch { return ''; }
};

const getToken = async () => {
  try {
    const user = auth.currentUser;
    return user ? await user.getIdToken() : null;
  } catch { return null; }
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const pollRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
    } catch { /* silent fail */ }
  }, []);

  // Initial fetch + polling
  useEffect(() => {
    fetchNotifications();
    pollRef.current = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markRead = async (notifId) => {
    const token = await getToken();
    if (!token) return;
    try {
      await fetch(`${API}/api/notifications/${notifId}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(prev =>
        prev.map(n => n.id === notifId ? { ...n, read: true } : n)
      );
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    setLoading(true);
    const token = await getToken();
    if (!token) { setLoading(false); return; }
    try {
      await fetch(`${API}/api/notifications/mark-all-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* silent */ }
    setLoading(false);
  };

  const handleNotifClick = async (notif) => {
    await markRead(notif.id);
    const meta = notif.metadata || {};
    if (notif.type === 'quote_approved' || notif.type === 'quote_viewed') {
      if (meta.leadId) navigate(`/leads/${meta.leadId}`);
      else navigate('/pipeline');
    } else if (notif.type === 'payment_received') {
      navigate('/invoices');
    } else if (notif.type === 'new_sms') {
      if (meta.conversationId) navigate(`/inbox?conversation=${meta.conversationId}`);
      else navigate('/inbox');
    } else if (notif.type === 'missed_call') {
      // Phase 1 / B2 — deep-link to the missed-call conversation
      if (meta.conversationId) navigate(`/inbox?conversation=${meta.conversationId}`);
      else navigate('/inbox');
    }
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(o => !o);
    if (!open) fetchNotifications(); // Refresh on open
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        title="Notifications"
        data-testid="notification-bell"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <Bell className="h-5 w-5 text-gray-600" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="font-semibold text-sm text-gray-900">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-xs font-normal text-gray-500">
                  {unreadCount} unread
                </span>
              )}
            </span>
            <div className="flex items-center gap-1.5">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  disabled={loading}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
                  title="Mark all as read"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  All read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No notifications yet</p>
                <p className="text-xs text-gray-300 mt-1">Events appear here in real time</p>
              </div>
            ) : (
              notifications.map(notif => {
                const { icon: Icon, color, bg } = TYPE_CONFIG[notif.type] || DEFAULT_CFG;
                return (
                  <div
                    key={notif.id}
                    onClick={() => handleNotifClick(notif)}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                      !notif.read ? 'bg-orange-50/50' : ''
                    }`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className={`${bg} p-1.5 rounded-full mt-0.5 shrink-0`}>
                      <Icon className={`h-3.5 w-3.5 ${color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900 leading-snug">{notif.title}</p>
                      <p className="text-xs text-gray-600 leading-snug mt-0.5 line-clamp-2">{notif.body}</p>
                      <p className="text-[11px] text-gray-400 mt-1">{formatTimeAgo(notif.createdAt)}</p>
                    </div>
                    {!notif.read && (
                      <div className="h-2 w-2 rounded-full bg-orange-400 mt-2 shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
